// "Warnings on route": what the hike page's folded section, the map card's
// chip and the planner's Alerts sheet show, from a routeAlertsForHike /
// routeAlertsForRoute result. Pure — the translator, units and locale are
// parameters — so every string is unit-tested without mounting anything.
//
// Wording rules (client, ROUTE-ALERTS.md): the aggregate is "warnings", never
// "hazards", and the word "reported" appears nowhere in this UI. Rows are
// shown in the order the server sends them (danger first, then by distance
// along the route, hazards and difficult sections mixed) — never re-sorted here.

import { HazardType, Passability, ConfirmationState, confirmationStateFromWire, hazardTypeFromWire, passabilityFromWire } from '../trailHazards/hazardTypes';
import { hazardTypeName, HazardTag, Translate } from '../trailHazards/hazardDetails';
import { GENERIC_WARNING_ICON, hazardIconName } from '../trailHazards/hazardSprite';
import { SacGradeTag, sacColour, sacGradeFromWire, sacGradeName, sacGradeTag, sacLevel, sectionRangeText } from './sacSections';

/**
 * A community hazard, or a difficult section (SAC T3+ terrain, from GraphHopper's
 * hike_rating). Anything else (a newer server's kind) is a generic warning.
 */
export type RouteAlertKind = 'hazard' | 'difficult_terrain' | 'unknown';
export type RouteAlertLevel = 'warning' | 'danger';

export interface RouteAlertHazard {
  id: string;
  /** Null for a type this bundle doesn't know (a newer server). */
  hazardType: HazardType | null;
  /** The raw wire type, lower-cased, for analytics even when unknown. */
  rawHazardType: string | null;
  passability: Passability | null;
  confirmationState: ConfirmationState | null;
}

export interface RouteAlert {
  id: string;
  kind: RouteAlertKind;
  /** The kind as sent, lower-cased (`hazard`), for analytics. */
  rawKind: string;
  level: RouteAlertLevel;
  latitude: number;
  longitude: number;
  /** Metres from the start of the route; null when it couldn't be computed. */
  distanceAlongRouteM: number | null;
  distanceFromRouteM: number | null;
  hazard: RouteAlertHazard | null;
  /** Difficult sections only (null for a hazard): the hardest SAC grade inside, 3-6. */
  sacGrade: number | null;
  startAlongRouteM: number | null;
  endAlongRouteM: number | null;
  lengthM: number | null;
  /** The section's geometry: Google polyline, precision 5, lat/lng. */
  encodedPolyline: string | null;
}

/**
 * One list entry: a hazard type or SAC grade, shown as its first alert (in the
 * server's order) plus how many more the group has ("+1 more").
 */
export interface RouteAlertCard {
  alert: RouteAlert;
  moreCount: number;
  groupKey: string;
}

export interface RouteAlerts {
  /** Every warning: the headline counts. */
  count: number;
  /** Every warning, in the server's order: what the maps draw. */
  alerts: RouteAlert[];
  /** What the lists show: one card per hazard type / SAC grade. */
  cards: RouteAlertCard[];
}

export const EMPTY_ROUTE_ALERTS: RouteAlerts = Object.freeze({ count: 0, alerts: [], cards: [] }) as RouteAlerts;

function finiteOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function lowerOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : null;
}

function normalizeHazard(raw: any): RouteAlertHazard | null {
  if (!raw || typeof raw !== 'object' || raw.id === null || raw.id === undefined) return null;
  return {
    id: String(raw.id),
    hazardType: hazardTypeFromWire(raw.hazardType),
    rawHazardType: lowerOrNull(raw.hazardType),
    passability: passabilityFromWire(raw.passability),
    confirmationState: confirmationStateFromWire(raw.confirmationState),
  };
}

function normalizeAlert(raw: any): RouteAlert | null {
  if (!raw || typeof raw !== 'object') return null;
  const latitude = finiteOrNull(raw.latitude);
  const longitude = finiteOrNull(raw.longitude);
  if (raw.id === null || raw.id === undefined || latitude === null || longitude === null) return null;
  const rawKind = lowerOrNull(raw.kind) ?? 'unknown';
  const hazard = normalizeHazard(raw.hazard);
  const sacGrade = sacGradeFromWire(raw.sacGrade);
  let kind: RouteAlertKind = 'unknown';
  if (rawKind === 'hazard' && hazard) kind = 'hazard';
  else if (rawKind === 'difficult_terrain' && sacGrade !== null) kind = 'difficult_terrain';
  const wireLevel = lowerOrNull(raw.level);
  return {
    id: String(raw.id),
    kind,
    rawKind,
    // A section without a level still gets its grade's colour.
    level: wireLevel === 'danger' || (!wireLevel && kind === 'difficult_terrain' && sacLevel(sacGrade!) === 'danger') ? 'danger' : 'warning',
    latitude,
    longitude,
    distanceAlongRouteM: finiteOrNull(raw.distanceAlongRouteM),
    distanceFromRouteM: finiteOrNull(raw.distanceFromRouteM),
    hazard,
    sacGrade: kind === 'difficult_terrain' ? sacGrade : null,
    startAlongRouteM: finiteOrNull(raw.startAlongRouteM),
    endAlongRouteM: finiteOrNull(raw.endAlongRouteM),
    lengthM: finiteOrNull(raw.lengthM),
    encodedPolyline: typeof raw.encodedPolyline === 'string' && raw.encodedPolyline ? raw.encodedPolyline : null,
  };
}

/**
 * The GraphQL `RouteAlerts` as the UI's model, in the server's order. A null
 * payload (a hike the viewer can't see) or anything malformed reads as no
 * warnings: the feature falls back to showing nothing, never an error.
 */
export function normalizeRouteAlerts(raw: any): RouteAlerts {
  if (!raw || typeof raw !== 'object') return { count: 0, alerts: [], cards: [] };
  const alerts = (Array.isArray(raw.alerts) ? raw.alerts : [])
    .map(normalizeAlert)
    .filter((alert: RouteAlert | null): alert is RouteAlert => alert !== null);
  // The rows are what can be shown; a count the list can't back would read wrong.
  return { count: alerts.length, alerts, cards: normalizeCards(raw.cards, alerts) };
}

function normalizeCard(raw: any): RouteAlertCard | null {
  if (!raw || typeof raw !== 'object') return null;
  const alert = normalizeAlert(raw.alert);
  if (!alert) return null;
  const more = Number(raw.moreCount);
  return {
    alert,
    moreCount: Number.isInteger(more) && more > 0 ? more : 0,
    groupKey: typeof raw.groupKey === 'string' && raw.groupKey ? raw.groupKey : alert.id,
  };
}

/**
 * The server's cards, in its order. A server without cards (or a broken card
 * list) gets one card per alert, so the list still shows everything.
 */
function normalizeCards(raw: unknown, alerts: RouteAlert[]): RouteAlertCard[] {
  const cards = Array.isArray(raw)
    ? raw.map(normalizeCard).filter((card): card is RouteAlertCard => card !== null)
    : [];
  if (cards.length > 0 || alerts.length === 0) return cards;
  return alerts.map((alert) => ({ alert, moreCount: 0, groupKey: alert.id }));
}

/** A card's extra line: "+1 more" / "+3 more"; null for none. */
export function moreOnRouteText(moreCount: number, t: Translate): string | null {
  if (!(moreCount > 0)) return null;
  return t('route_alerts.more', moreCount);
}

/* ---------- wording ---------- */

/** "1 warning on route" / "3 warnings on route" — the hike page's folded section. */
export function warningsOnRouteText(count: number, t: Translate): string {
  return t('route_alerts.section_title', count);
}

/** "1 warning" / "3 warnings" — the map card's chip. */
export function warningsChipText(count: number, t: Translate): string {
  return t('route_alerts.chip', count);
}

/** "Alerts, 1 warning" / "Alerts, 3 warnings" — the planner button's accessible name. */
export function alertsButtonA11yLabel(count: number, t: Translate): string {
  return t('route_alerts.button_a11y', count);
}

/** The number on the planner button's badge: "9+" past nine, like an app-icon badge. */
export function badgeText(count: number): string {
  if (!(count > 0)) return '';
  return count > 9 ? '9+' : String(Math.floor(count));
}

export interface DistanceOptions {
  metric: boolean;
  locale?: string;
}

const METRES_PER_MILE = 1609.344;

/**
 * "at 3.2 km" / "at 2 mi" in the viewer's units: one decimal under 100, whole
 * numbers from there, a trailing ".0" dropped. Null when unknown.
 */
export function distanceAlongText(metres: number | null | undefined, { metric, locale }: DistanceOptions, t: Translate): string | null {
  if (metres === null || metres === undefined || !Number.isFinite(metres) || metres < 0) return null;
  const value = metric ? metres / 1000 : metres / METRES_PER_MILE;
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat(locale || 'en', { maximumFractionDigits: value < 100 ? 1 : 0 }).format(value);
  } catch {
    formatted = (value < 100 ? Math.round(value * 10) / 10 : Math.round(value)).toString();
  }
  return metric
    ? t('route_alerts.at_km', { distance: formatted })
    : t('route_alerts.at_mi', { distance: formatted });
}

/**
 * The rows' one-word tag: "Blocked" (red) / "Passable" (orange). Shorter than
 * the details panel's "Blocked · find another way" / "Can get past · take
 * care", which keeps its own (client feedback 2026-10-20). The rows show no
 * confirmed / unconfirmed state.
 */
export function rowPassabilityTag(passability: Passability, t: Translate): HazardTag {
  return passability === 'blocked'
    ? { kind: 'blocked', text: t('route_alerts.tag_blocked') }
    : { kind: 'passable', text: t('route_alerts.tag_passable') };
}

/* ---------- rows ---------- */

export interface RouteAlertRowView {
  id: string;
  kind: RouteAlertKind;
  level: RouteAlertLevel;
  /** The sprite icon: `<slug>_<passability>`, or the generic warning (unused for a section). */
  iconName: string;
  /** A section's terrain triangle colour (orange T3-T4 / red T5-T6); null for a hazard. */
  terrainColour: string | null;
  title: string;
  /** "Blocked" / "Passable". */
  passabilityTag: HazardTag | null;
  /** A section's "T4", orange or red. */
  gradeTag: SacGradeTag | null;
  /** "at 3.2 km", or a section's "2.4–3.1 km · 700 m". */
  distanceText: string | null;
  /** The hazard the row opens; null for a kind with no details panel. */
  hazardId: string | null;
  /** A difficult section, which the row shows on the map. */
  sectionId: string | null;
  /** The row does something when picked (a chevron): a hazard or a section. */
  selectable: boolean;
  /** A card's other alerts of the same type / grade (0 for a plain row). */
  moreCount: number;
  /** "+N more", or null. */
  moreText: string | null;
  /** The card's group ("hazard:tree_down", "sac:4"); null for a plain row. */
  groupKey?: string | null;
  latitude: number;
  longitude: number;
  alert: RouteAlert;
}

/** A difficult section's row: its grade's name, icon colour and tag, and where it runs. */
function buildSectionRow(alert: RouteAlert, grade: number, options: DistanceOptions, t: Translate): RouteAlertRowView {
  return {
    id: alert.id,
    kind: 'difficult_terrain',
    level: alert.level,
    iconName: GENERIC_WARNING_ICON,
    terrainColour: sacColour(grade),
    title: sacGradeName(grade, t),
    passabilityTag: null,
    gradeTag: sacGradeTag(grade),
    distanceText: sectionRangeText(alert, options, t) ?? distanceAlongText(alert.distanceAlongRouteM, options, t),
    hazardId: null,
    sectionId: alert.id,
    selectable: true,
    moreCount: 0,
    moreText: null,
    latitude: alert.latitude,
    longitude: alert.longitude,
    alert,
  };
}

export function buildRouteAlertRow(alert: RouteAlert, options: DistanceOptions, t: Translate): RouteAlertRowView {
  if (alert.kind === 'difficult_terrain' && alert.sacGrade !== null) return buildSectionRow(alert, alert.sacGrade, options, t);
  const hazard = alert.kind === 'hazard' ? alert.hazard : null;
  const knownType = hazard?.hazardType ?? null;
  // A blocked hazard is DANGER; if the hazard has no passability (a newer
  // server's shape), the level still picks the right colour.
  const passability: Passability | null = hazard?.passability ?? (alert.level === 'danger' ? 'blocked' : null);
  return {
    id: alert.id,
    kind: alert.kind,
    level: alert.level,
    iconName: knownType ? hazardIconName(knownType, passability) : GENERIC_WARNING_ICON,
    terrainColour: null,
    title: knownType ? hazardTypeName(knownType, t) : t('route_alerts.generic_warning'),
    passabilityTag: hazard && passability ? rowPassabilityTag(passability, t) : null,
    gradeTag: null,
    distanceText: distanceAlongText(alert.distanceAlongRouteM, options, t),
    hazardId: hazard ? hazard.id : null,
    sectionId: null,
    selectable: !!hazard,
    moreCount: 0,
    moreText: null,
    latitude: alert.latitude,
    longitude: alert.longitude,
    alert,
  };
}

/** One row per alert, in the order received. */
export function buildRouteAlertRows(alerts: RouteAlerts, options: DistanceOptions, t: Translate): RouteAlertRowView[] {
  return alerts.alerts.map((alert) => buildRouteAlertRow(alert, options, t));
}

/**
 * The lists' rows: one per card, in the server's order — its alert's row
 * exactly, plus "+N more" when the group has more.
 */
export function buildRouteAlertCardRows(alerts: RouteAlerts, options: DistanceOptions, t: Translate): RouteAlertRowView[] {
  return (alerts.cards ?? []).map((card) => ({
    ...buildRouteAlertRow(card.alert, options, t),
    groupKey: card.groupKey,
    moreCount: card.moreCount,
    moreText: moreOnRouteText(card.moreCount, t),
  }));
}
